import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { firstValueFrom, Observable } from 'rxjs';
import { AuthHttpClient } from '../http/AuthHttpClient';
import { ApiService } from '../services/api.service';
import { environment as env } from '../../environments/environment';
import { ILead } from './lead.interface';
import { ECustomerType } from './leads-list/leads-main.component';
import { UserAccountType } from '../data-types/user-data';
import { Store } from '../store';
import { UserService } from '../services/user.service';
import { LogChangesService } from '../services/log-changes.service';

@Injectable({
    providedIn: 'root',
})
export class LeadService {
    readonly userAccountToCustomerTypeMap = {
        [UserAccountType.Trade]: ECustomerType.Trade,
        [UserAccountType.Customer]: ECustomerType.Private,
        [UserAccountType.Guest]: ECustomerType.Unknown,
    };

    readonly customerToUserAccountTypeMap = {
        [ECustomerType.Trade]: UserAccountType.Trade,
        [ECustomerType.Private]: UserAccountType.Customer,
        [ECustomerType.Unknown]: UserAccountType.Guest,
    };

    constructor(
        private apiService: ApiService,
        private http: AuthHttpClient,
        private store: Store,
        private userService: UserService,
        private logChangesService: LogChangesService
    ) {}

    getLeads(params = {}) {
        const dataToSend = {
            params,
        };

        return this.http.post(env.apiPath + 'users/lead', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return {
                    totalRecords: data.data.totalRecords,
                    leads: data.data.leads.map((lead) => {
                        return {
                            ...lead,
                            name: `${lead.firstname} ${lead.surname}`,
                            customerType: this.userAccountToCustomerTypeMap[lead.accountType],
                            createdAt: new Date(lead.createdAt),
                        };
                    }),
                };
            }, catchError(this.apiService.handleError))
        );
    }

    getLeadById(leadId: string): Observable<ILead> {
        return this.http.get(env.apiPath + `users/lead/${leadId}`, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                const lead = data.data;
                return {
                    ...lead,
                    customerType: this.userAccountToCustomerTypeMap[lead.accountType],
                    createdAt: new Date(lead.createdAt),
                    add1: lead.add1 === 'undefined' || lead.add1 === 'null' ? null : lead.add1,
                    add2: lead.add2 === 'undefined' || lead.add2 === 'null' ? null : lead.add2,
                    add3: lead.add3 === 'undefined' || lead.add3 === 'null' ? null : lead.add3,
                };
            }, catchError(this.apiService.handleError))
        );
    }

    async converLead(lead, isWarm): Promise<void> {
        let res = await firstValueFrom(this.userService.findOne(lead.id));
        const leadData = res.data;
        leadData.contact.isWarm = isWarm;
        await firstValueFrom(this.userService.update(leadData));
        lead.isWarm = isWarm;
    }

    async archiveLead(lead): Promise<void> {
        let res = await firstValueFrom(this.userService.findOne(lead.id));
        const leadData = res.data;
        leadData.isArchived = 1;
        await firstValueFrom(this.userService.update(leadData));
    }

    async placeLeadOnHold(lead): Promise<void> {
        let res = await firstValueFrom(this.userService.findOne(lead.id));
        const leadData = res.data;
        leadData.onHold = 1;
        await firstValueFrom(this.userService.update(leadData));
    }

    async convertLeadToUser(leadId): Promise<void> {
        await firstValueFrom(
            this.http
                .get(env.apiPath + `users/convert-lead-to-user/${leadId}`, this.apiService.getHttpOptions())
                .pipe(catchError(this.apiService.handleError))
        );
    }

    getLogChanges(leadId: string): Observable<any> {
        return this.logChangesService.getLogsForCustomer(leadId);
    }
}
